
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Users } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import EveningPlannerModal from './EveningPlannerModal';

const COLLAGE_TILES = [
  { emoji: '🎳', label: 'Bowling', style: 'top-2 left-[18%] w-48 h-60 -rotate-6 z-30', overlay: 'from-[#7c3aed] to-[#ec4899]', bgImage: '/hero/bowling.jpg', objectPosition: 'center' },
  { emoji: '🏎️', label: 'Karting', style: 'top-7 right-0 w-52 h-48 rotate-[4deg] z-20', overlay: 'from-[#ef4444] to-[#f97316]', bgImage: '/hero/karting.jpg', objectPosition: 'center' },
  { emoji: '🔐', label: 'Escape Game', style: 'bottom-6 left-0 w-44 h-52 rotate-[3deg] z-10', overlay: 'from-[#1e293b] to-[#4f46e5]', bgImage: '/hero/escape-game.webp', objectPosition: 'center' },
  { emoji: '🎾', label: 'Padel', style: 'bottom-0 right-[14%] w-56 h-48 -rotate-[3deg] z-10', overlay: 'from-[#16a34a] to-[#0d9488]', bgImage: '/hero/padel.jpg', objectPosition: 'center top' },
];


const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (delay = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] } }),
};

const Hero = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const shouldReduce = useReducedMotion();
  const [plannerOpen, setPlannerOpen] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = inputRef.current?.value?.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <div className="px-4 sm:px-6 pt-6 pb-0">
      <div
        className="relative max-w-[1280px] mx-auto rounded-3xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FFE0D3 0%, #FFFFFF 45%, #FFF1EB 100%)' }}
      >
        {/* Decorative blobs */}
        <div className="absolute w-96 h-96 rounded-full pointer-events-none" style={{ background: 'rgba(255,107,53,.10)', top: '-150px', right: '-120px' }} />
        <div className="absolute w-72 h-72 rounded-full pointer-events-none" style={{ background: 'rgba(247,147,30,.10)', bottom: '-120px', left: '-100px' }} />

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center px-4 sm:px-10 lg:px-16 py-10 lg:py-20">
          {/* Left: copy + search */}
          <motion.div
            initial={shouldReduce ? false : 'hidden'}
            animate="visible"
            variants={{ hidden: {}, visible: {} }}
          >
            {/* Eyebrow pill */}
            <motion.span
              custom={0}
              variants={fadeUp}
              initial={shouldReduce ? false : 'hidden'}
              animate="visible"
              className="inline-flex items-center gap-2 bg-white rounded-pill px-3.5 py-1.5 shadow-brand-sm text-sm font-medium text-brand-text mb-5"
            >
              <span className="w-5 h-5 rounded-full bg-brand-success flex items-center justify-center text-white text-xs font-bold flex-shrink-0">✓</span>
              <span>Activités indoor à Paris &amp; Île-de-France</span>
            </motion.span>

            {/* H1 */}
            <motion.h1
              custom={0.08}
              variants={fadeUp}
              initial={shouldReduce ? false : 'hidden'}
              animate="visible"
              className="font-heading font-black text-brand-dark leading-[1.05] tracking-[-0.025em] mb-5"
              style={{ fontSize: 'clamp(36px, 4.2vw, 56px)' }}
            >
              Réservez vos&nbsp;
              <em className="not-italic text-brand-primary relative">
                activités indoor
                <span
                  className="absolute left-[4%] right-[4%] -bottom-0.5 h-2.5 rounded-pill -z-10"
                  style={{ background: 'rgba(255,107,53,.22)' }}
                />
              </em>
              &nbsp;préférées.
            </motion.h1>

            {/* Lede */}
            <motion.p
              custom={0.15}
              variants={fadeUp}
              initial={shouldReduce ? false : 'hidden'}
              animate="visible"
              className="text-lg leading-relaxed text-brand-text-muted mb-8 max-w-[50ch]"
            >
              Bowling, karting, escape game, padel… On t'aide à trouver l'activité parfaite pour ta prochaine sortie en groupe : vote, valide, joue.
            </motion.p>

            {/* Search bar */}
            <motion.form
              custom={0.22}
              variants={fadeUp}
              initial={shouldReduce ? false : 'hidden'}
              animate="visible"
              onSubmit={handleSearch}
              className="flex flex-col sm:flex-row gap-2 p-2 bg-white rounded-xl shadow-brand-lg border border-brand-border max-w-[540px]"
            >
              <div className="flex flex-1 items-center gap-2.5 px-3.5">
                <Search size={18} className="text-brand-text-subtle flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Que veux-tu faire ce soir ?"
                  className="flex-1 border-0 outline-none font-sans text-[15px] text-brand-text bg-transparent py-2.5 sm:py-3 placeholder:text-brand-text-subtle"
                />
              </div>
              <button
                type="submit"
                className="bg-brand-primary hover:bg-brand-primary-600 text-white rounded-lg px-5 py-3 sm:py-0 w-full sm:w-auto font-semibold text-[15px] shadow-brand-primary flex items-center justify-center gap-2 transition-colors duration-200 whitespace-nowrap"
              >
                Rechercher
                <ArrowRight size={16} />
              </button>
            </motion.form>

            {/* Evening planner CTA */}
            <motion.button
              custom={0.30}
              variants={fadeUp}
              initial={shouldReduce ? false : 'hidden'}
              animate="visible"
              onClick={() => setPlannerOpen(true)}
              className="mt-4 inline-flex items-center gap-2 text-[14px] text-brand-text-muted hover:text-brand-primary transition-colors duration-200 group"
            >
              <span className="w-6 h-6 rounded-full bg-brand-primary/10 group-hover:bg-brand-primary/20 flex items-center justify-center transition-colors duration-200">
                <Users size={13} className="text-brand-primary" />
              </span>
              Planifier une soirée en groupe
              <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform duration-200" />
            </motion.button>

          </motion.div>

          {/* Right: collage (desktop only) */}
          <motion.div
            initial={shouldReduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block relative h-[480px]"
          >
            {COLLAGE_TILES.map(({ emoji, label, style, overlay, bgImage, objectPosition }) => (
              <div
                key={label}
                className={`absolute rounded-2xl overflow-hidden shadow-brand-lg ${style}`}
                style={{ transition: 'transform .4s cubic-bezier(0.25,0.46,0.45,0.94)' }}
              >
                <img
                  src={bgImage}
                  alt={label}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition }}
                  loading="eager"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${overlay} opacity-55`} />
                <div className="absolute left-3.5 bottom-3 text-white font-heading font-bold" style={{ textShadow: '0 2px 8px rgba(0,0,0,.5)' }}>
                  <span className="block text-[26px] mb-0.5" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.4))' }}>{emoji}</span>
                  <span className="text-[15px]">{label}</span>
                </div>
              </div>
            ))}

          </motion.div>
        </div>
      </div>

      <EveningPlannerModal isOpen={plannerOpen} onClose={() => setPlannerOpen(false)} />
    </div>
  );
};

export default Hero;
